import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useState } from "react";
import { Flex } from "../../components/Flex";
import { Image } from "../../components/Image";
import accept from "../../assets/manual-accept.svg";
import reject from "../../assets/manual-reject.svg";
import reset from "../../assets/manual-reset.svg";
import { useManualAcceptReject } from "../../data/useManualAcceptReject";
import { individualCheckQueryKey } from "../../data/useIndividualCheck";
import { monitoringActivityQueryKey } from "../../data/useMonitoringActivityView";
import { showError } from "../../utils/error-handling";
import { queryClient } from "../../queryClient";
import { useAuth } from "../../context/AuthContext";

interface Props {
  title: string;
  id: number;
  isMonitoring: boolean;
  showReset?: boolean;
  showAccept?: boolean;
  showReject?: boolean;
  resetConfirmationMessage?: string;
  /** SEG permission codename gating Accept/Reject/Reset (e.g.
   * "screening.review_activity", "monitoring.review_activity"). Omit to leave
   * ungated — this component is also shared by Applications' mandate-check
   * screens, which have no matching codename in the catalogue. */
  permissionCodename?: string;
  /** Tooltip shown on Accept/Reject/Reset when permissionCodename is set and missing. */
  permissionDeniedMessage?: string;
}

export const ManualAcceptReject = (props: Props) => {
  const {
    title,
    id,
    isMonitoring,
    showReset = false,
    showAccept = true,
    showReject = true,
    resetConfirmationMessage,
    permissionCodename,
    permissionDeniedMessage = "You don't have permission to do this",
  } = props;

  const { hasPermission } = useAuth();
  const isPermitted = permissionCodename
    ? hasPermission(permissionCodename)
    : true;

  const [isManualAcceptRejectOpen, setIsManualAcceptRejectOpen] =
    useState(false);
  const [isManualAcceptRejectSuccess, setIsManualAcceptRejectSuccess] =
    useState(false);
  const [action, setAction] = useState("");
  const [isLoadingAction, setIsLoadingAction] = useState(false);

  const { mutate: manualAcceptReject } = useManualAcceptReject();

  const handleManualAcceptOrReject = (comment: string) => {
    setIsLoadingAction(true);
    const isMonitoringAvailable = isMonitoring ? isMonitoring : false;

    manualAcceptReject(
      isMonitoringAvailable
        ? {
            id,
            action,
            comment,
            comment_for: "IndividualMonitoringActivity",
            isMonitoring: isMonitoringAvailable,
          }
        : {
            id,
            action,
            comment,
            comment_for: "IndividualCheck",
            isMonitoring: isMonitoringAvailable,
          },
      {
        onSuccess: () => {
          setIsLoadingAction(false);
          setIsManualAcceptRejectOpen(false);
          setIsManualAcceptRejectSuccess(true);
        },
        onError: (err: any) => {
          setIsLoadingAction(false);
          showError(
            err,
            `A problem occurred while manually ${
              action === "accept"
                ? "accepting"
                : action === "reject"
                ? "rejecting"
                : "resetting"
            } check.`,
          );
        },
      },
    );
  };

  const handleSuccessClose = () => {
    setIsManualAcceptRejectSuccess(false);
    // the two hooks are keyed separately, so invalidate the one this instance
    // actually accepted or rejected against
    queryClient.invalidateQueries(
      isMonitoring
        ? monitoringActivityQueryKey(id)
        : individualCheckQueryKey(id),
    );
    queryClient.invalidateQueries(["individual"]);
  };

  const defaultResetMessage = `Any existing information for this check will be removed and the client may be required to complete the check again.`;

  return (
    <>
      <Flex mr={5}>
        {showReset && (
          <Button
            px={5}
            mr={4}
            variant="secondary"
            fontWeight="500"
            color="blue.primary"
            isDisabled={!isPermitted}
            title={isPermitted ? undefined : permissionDeniedMessage}
            onClick={() => {
              setAction("reset");
              setIsManualAcceptRejectOpen(true);
            }}
          >
            <Image src={reset} alt="reset" height="20px" width="auto" mr={1} />
            Reset
          </Button>
        )}

        {showReject && (
          <Button
            px={5}
            variant="secondary"
            color="red.primary"
            mr={2}
            fontWeight="500"
            isDisabled={!isPermitted}
            title={isPermitted ? undefined : permissionDeniedMessage}
            onClick={() => {
              setAction("reject");
              setIsManualAcceptRejectOpen(true);
            }}
          >
            <Image
              src={reject}
              alt="reject"
              height="20px"
              width="auto"
              mr={1}
            />
            Reject
          </Button>
        )}

        {showAccept && (
          <Button
            px={5}
            mr={4}
            variant="secondary"
            fontWeight="500"
            color="blue.primary"
            isDisabled={!isPermitted}
            title={isPermitted ? undefined : permissionDeniedMessage}
            onClick={() => {
              setAction("accept");
              setIsManualAcceptRejectOpen(true);
            }}
          >
            <Image
              src={accept}
              alt="accept"
              height="20px"
              width="auto"
              mr={1}
            />
            Accept
          </Button>
        )}
      </Flex>

      <Modal
        isClosingDisabled={isLoadingAction}
        isOpen={isManualAcceptRejectOpen}
        onClose={() => setIsManualAcceptRejectOpen(false)}
        onModalConfirmClick={handleManualAcceptOrReject}
        title={`Are you sure you want to ${action} the ${title}?`}
        content={
          action === "reset"
            ? resetConfirmationMessage || defaultResetMessage
            : `Please leave a comment to confirm why you are ${
                action === "accept" ? "accepting" : "rejecting"
              } this check.`
        }
        hasSecondaryButton
        secondaryButtonText="Cancel"
        width="450px"
        primaryButtonText="Continue"
        primaryButtonVariant="primary"
        hasCommentBox={action !== "reset"}
      />

      <Modal
        isOpen={isManualAcceptRejectSuccess}
        onClose={handleSuccessClose}
        hasPrimaryButton={false}
        title={`${title} ${
          action === "accept"
            ? "Accepted"
            : action === "reject"
            ? "Rejected"
            : "Reset"
        }`}
        content={`Thank you for ${
          action === "accept"
            ? "accepting"
            : action === "reject"
            ? "rejecting"
            : "resetting"
        } this check. The status has been updated.`}
        hasSecondaryButton
        secondaryButtonText="Close"
        width="450px"
      />
    </>
  );
};
